import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    FormGroup,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    Tab,
    Tabs,
    Snackbar,
    LinearProgress
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

// icons
import {
    IconSettings,
    IconKey,
    IconPlus,
    IconTrash,
    IconCopy,
    IconRefresh,
    IconCircleCheck,
    IconWebhook,
    IconCloud as IconBrandAws,
    IconCloud as IconBrandAzure,
    IconCloud as IconBrandGoogle
} from '@tabler/icons-react';

// ==============================|| LAMBDA SETTINGS ||============================== //

const LambdaSettings = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [tabValue, setTabValue] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [apiKeyDialog, setApiKeyDialog] = useState(false);
    const [webhookDialog, setWebhookDialog] = useState(false);

    // Settings state
    const [settings, setSettings] = useState({
        // General settings
        defaultMemory: '256Mi',
        defaultTimeout: 30,
        defaultRuntime: 'python3.9',
        maxConcurrency: 100,
        coldStartOptimization: true,
        autoScaling: true,
        loggingLevel: 'INFO',

        // Security settings
        apiAuthentication: true,
        encryptEnvironmentVars: true,
        enableCORS: true,
        allowedOrigins: ['https://app.example.com', 'https://api.example.com'],
        ipWhitelist: [],

        // Notifications
        emailNotifications: true,
        slackNotifications: false,
        webhookNotifications: false,
        notificationEmail: 'admin@example.com',
        slackWebhook: '',

        // Resource limits
        maxFunctionsPerUser: 50,
        maxExecutionsPerDay: 100000,
        maxStoragePerFunction: '1Gi',
        maxExecutionTime: 900,

        // Cloud provider
        cloudProvider: 'aws',
        awsRegion: 'us-east-1',
        awsLambdaRole: 'arn:aws:iam::123456789:role/lambda-execution-role'
    });

    // API Keys state
    const [apiKeys, setApiKeys] = useState([
        {
            id: 'key_1',
            name: 'Production API Key',
            key: 'pk_live_51234567890abcdef',
            created: '2024-07-15',
            lastUsed: '2024-07-17',
            status: 'active'
        },
        {
            id: 'key_2',
            name: 'Development API Key',
            key: 'pk_test_abcdef1234567890',
            created: '2024-07-10',
            lastUsed: '2024-07-16',
            status: 'active'
        }
    ]);

    // Webhooks state
    const [webhooks, setWebhooks] = useState([
        {
            id: 'webhook_1',
            url: 'https://app.example.com/webhooks/lambda',
            events: ['function.deployed', 'function.failed'],
            status: 'active',
            secret: 'whsec_1234567890'
        }
    ]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSettingChange = (setting, value) => {
        setSettings({ ...settings, [setting]: value });
    };

    const handleSaveSettings = () => {
        // Save settings logic
        setSnackbar({
            open: true,
            message: 'Settings saved successfully',
            severity: 'success'
        });
    };

    const handleCreateApiKey = (keyData) => {
        const newKey = {
            id: `key_${Date.now()}`,
            key: `pk_${Math.random().toString(36).substr(2, 20)}`,
            created: new Date().toISOString().split('T')[0],
            lastUsed: null,
            status: 'active',
            ...keyData
        };
        setApiKeys([...apiKeys, newKey]);
        setApiKeyDialog(false);
        setSnackbar({
            open: true,
            message: 'API key created successfully',
            severity: 'success'
        });
    };

    const handleDeleteApiKey = (keyId) => {
        if (window.confirm('Are you sure you want to delete this API key?')) {
            setApiKeys(apiKeys.filter((key) => key.id !== keyId));
            setSnackbar({
                open: true,
                message: 'API key deleted successfully',
                severity: 'success'
            });
        }
    };

    const handleCreateWebhook = (webhookData) => {
        const newWebhook = {
            id: `webhook_${Date.now()}`,
            secret: `whsec_${Math.random().toString(36).substr(2, 20)}`,
            status: 'active',
            ...webhookData
        };
        setWebhooks([...webhooks, newWebhook]);
        setWebhookDialog(false);
        setSnackbar({
            open: true,
            message: 'Webhook created successfully',
            severity: 'success'
        });
    };

    return (
        <>
            <MainCard
                title={
                    <Box display="flex" alignItems="center" gap={1}>
                        <IconSettings />
                        <Typography variant="h3">Lambda Settings</Typography>
                    </Box>
                }
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="General" />
                        <Tab label="Security" />
                        <Tab label="API Keys" />
                        <Tab label="Webhooks" />
                        <Tab label="Notifications" />
                        <Tab label="Limits & Quotas" />
                    </Tabs>
                </Box>

                {/* General Settings Tab */}
                {tabValue === 0 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Default Configuration
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Default Memory</InputLabel>
                                                <Select
                                                    value={settings.defaultMemory}
                                                    label="Default Memory"
                                                    onChange={(e) => handleSettingChange('defaultMemory', e.target.value)}
                                                >
                                                    <MenuItem value="128Mi">128 MB</MenuItem>
                                                    <MenuItem value="256Mi">256 MB</MenuItem>
                                                    <MenuItem value="512Mi">512 MB</MenuItem>
                                                    <MenuItem value="1Gi">1 GB</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Default Timeout (seconds)"
                                                type="number"
                                                value={settings.defaultTimeout}
                                                onChange={(e) => handleSettingChange('defaultTimeout', parseInt(e.target.value))}
                                                inputProps={{ min: 1, max: 900 }}
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Default Runtime</InputLabel>
                                                <Select
                                                    value={settings.defaultRuntime}
                                                    label="Default Runtime"
                                                    onChange={(e) => handleSettingChange('defaultRuntime', e.target.value)}
                                                >
                                                    <MenuItem value="python3.9">Python 3.9</MenuItem>
                                                    <MenuItem value="python3.8">Python 3.8</MenuItem>
                                                    <MenuItem value="nodejs18">Node.js 18</MenuItem>
                                                    <MenuItem value="nodejs16">Node.js 16</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Logging Level</InputLabel>
                                                <Select
                                                    value={settings.loggingLevel}
                                                    label="Logging Level"
                                                    onChange={(e) => handleSettingChange('loggingLevel', e.target.value)}
                                                >
                                                    <MenuItem value="DEBUG">DEBUG</MenuItem>
                                                    <MenuItem value="INFO">INFO</MenuItem>
                                                    <MenuItem value="WARNING">WARNING</MenuItem>
                                                    <MenuItem value="ERROR">ERROR</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Performance & Scaling
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Max Concurrency"
                                                type="number"
                                                value={settings.maxConcurrency}
                                                onChange={(e) => handleSettingChange('maxConcurrency', parseInt(e.target.value))}
                                                helperText="Maximum concurrent executions per function"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.coldStartOptimization}
                                                        onChange={(e) => handleSettingChange('coldStartOptimization', e.target.checked)}
                                                    />
                                                }
                                                label="Cold Start Optimization"
                                            />
                                            <Typography variant="caption" display="block" color="textSecondary">
                                                Keep functions warm to reduce cold start latency
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={settings.autoScaling}
                                                        onChange={(e) => handleSettingChange('autoScaling', e.target.checked)}
                                                    />
                                                }
                                                label="Auto Scaling"
                                            />
                                            <Typography variant="caption" display="block" color="textSecondary">
                                                Automatically scale functions based on load
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Cloud Provider Configuration
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <InputLabel>Cloud Provider</InputLabel>
                                                <Select
                                                    value={settings.cloudProvider}
                                                    label="Cloud Provider"
                                                    onChange={(e) => handleSettingChange('cloudProvider', e.target.value)}
                                                >
                                                    <MenuItem value="aws">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <IconBrandAws size={20} />
                                                            AWS Lambda
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="azure">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <IconBrandAzure size={20} />
                                                            Azure Functions
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="gcp">
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <IconBrandGoogle size={20} />
                                                            Google Cloud Functions
                                                        </Box>
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        {settings.cloudProvider === 'aws' && (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="AWS Region"
                                                        value={settings.awsRegion}
                                                        onChange={(e) => handleSettingChange('awsRegion', e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Lambda Execution Role ARN"
                                                        value={settings.awsLambdaRole}
                                                        onChange={(e) => handleSettingChange('awsLambdaRole', e.target.value)}
                                                    />
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Security Tab */}
                {tabValue === 1 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Authentication & Authorization
                                    </Typography>

                                    <FormGroup sx={{ mt: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.apiAuthentication}
                                                    onChange={(e) => handleSettingChange('apiAuthentication', e.target.checked)}
                                                />
                                            }
                                            label="Require API Authentication"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.encryptEnvironmentVars}
                                                    onChange={(e) => handleSettingChange('encryptEnvironmentVars', e.target.checked)}
                                                />
                                            }
                                            label="Encrypt Environment Variables"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings.enableCORS}
                                                    onChange={(e) => handleSettingChange('enableCORS', e.target.checked)}
                                                />
                                            }
                                            label="Enable CORS"
                                        />
                                    </FormGroup>

                                    {settings.enableCORS && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Allowed Origins
                                            </Typography>
                                            <List>
                                                {settings.allowedOrigins.map((origin, index) => (
                                                    <ListItem key={index} dense>
                                                        <ListItemText primary={origin} />
                                                        <ListItemSecondaryAction>
                                                            <IconButton edge="end" size="small">
                                                                <IconTrash size={16} />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                ))}
                                            </List>
                                            <Button variant="outlined" size="small" startIcon={<IconPlus />}>
                                                Add Origin
                                            </Button>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Network Security
                                    </Typography>

                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <Typography variant="caption">
                                            Configure IP whitelist to restrict access to your Lambda functions
                                        </Typography>
                                    </Alert>

                                    <Typography variant="subtitle2" gutterBottom>
                                        IP Whitelist
                                    </Typography>
                                    <List>
                                        {settings.ipWhitelist.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No IP restrictions"
                                                    secondary="All IPs are allowed to access your functions"
                                                />
                                            </ListItem>
                                        ) : (
                                            settings.ipWhitelist.map((ip, index) => (
                                                <ListItem key={index} dense>
                                                    <ListItemText primary={ip} />
                                                    <ListItemSecondaryAction>
                                                        <IconButton edge="end" size="small">
                                                            <IconTrash size={16} />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))
                                        )}
                                    </List>
                                    <Button variant="outlined" size="small" startIcon={<IconPlus />}>
                                        Add IP Address
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* API Keys Tab */}
                {tabValue === 2 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                        <Typography variant="h5">API Keys</Typography>
                                        <Button variant="contained" startIcon={<IconPlus />} onClick={() => setApiKeyDialog(true)}>
                                            Create API Key
                                        </Button>
                                    </Box>

                                    <List>
                                        {apiKeys.map((apiKey) => (
                                            <React.Fragment key={apiKey.id}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconKey />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography variant="subtitle1">{apiKey.name}</Typography>
                                                                <Chip
                                                                    label={apiKey.status}
                                                                    size="small"
                                                                    color={apiKey.status === 'active' ? 'success' : 'default'}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    component="span"
                                                                    sx={{ fontFamily: 'monospace' }}
                                                                >
                                                                    {apiKey.key}
                                                                </Typography>
                                                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                                    Created: {apiKey.created} • Last used: {apiKey.lastUsed || 'Never'}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <Box display="flex" gap={1}>
                                                        <Tooltip title="Copy API Key">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigator.clipboard.writeText(apiKey.key)}
                                                            >
                                                                <IconCopy size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" onClick={() => handleDeleteApiKey(apiKey.id)}>
                                                                <IconTrash size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Webhooks Tab */}
                {tabValue === 3 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                        <Typography variant="h5">Webhooks</Typography>
                                        <Button variant="contained" startIcon={<IconPlus />} onClick={() => setWebhookDialog(true)}>
                                            Create Webhook
                                        </Button>
                                    </Box>

                                    <List>
                                        {webhooks.map((webhook) => (
                                            <React.Fragment key={webhook.id}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <IconWebhook />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Typography variant="subtitle1">{webhook.url}</Typography>
                                                                <Chip
                                                                    label={webhook.status}
                                                                    size="small"
                                                                    color={webhook.status === 'active' ? 'success' : 'default'}
                                                                />
                                                            </Box>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Box display="flex" gap={1} mt={0.5}>
                                                                    {webhook.events.map((event) => (
                                                                        <Chip key={event} label={event} size="small" variant="outlined" />
                                                                    ))}
                                                                </Box>
                                                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                                                    Secret: {webhook.secret}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <Box display="flex" gap={1}>
                                                        <Tooltip title="Test Webhook">
                                                            <IconButton size="small">
                                                                <IconRefresh size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small">
                                                                <IconTrash size={18} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Notifications Tab */}
                {tabValue === 4 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Email Notifications
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.emailNotifications}
                                                onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                                            />
                                        }
                                        label="Enable Email Notifications"
                                        sx={{ mb: 2 }}
                                    />

                                    {settings.emailNotifications && (
                                        <TextField
                                            fullWidth
                                            label="Notification Email"
                                            value={settings.notificationEmail}
                                            onChange={(e) => handleSettingChange('notificationEmail', e.target.value)}
                                            helperText="Email address to receive notifications"
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Slack Notifications
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={settings.slackNotifications}
                                                onChange={(e) => handleSettingChange('slackNotifications', e.target.checked)}
                                            />
                                        }
                                        label="Enable Slack Notifications"
                                        sx={{ mb: 2 }}
                                    />

                                    {settings.slackNotifications && (
                                        <TextField
                                            fullWidth
                                            label="Slack Webhook URL"
                                            value={settings.slackWebhook}
                                            onChange={(e) => handleSettingChange('slackWebhook', e.target.value)}
                                            helperText="Your Slack incoming webhook URL"
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Notification Events
                                    </Typography>

                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                        Select which events should trigger notifications
                                    </Typography>

                                    <FormGroup>
                                        <FormControlLabel control={<Switch defaultChecked />} label="Function deployment success" />
                                        <FormControlLabel control={<Switch defaultChecked />} label="Function deployment failure" />
                                        <FormControlLabel control={<Switch defaultChecked />} label="Function execution errors" />
                                        <FormControlLabel control={<Switch />} label="High error rate alerts" />
                                        <FormControlLabel control={<Switch />} label="Rate limit exceeded" />
                                        <FormControlLabel control={<Switch />} label="Cold start warnings" />
                                    </FormGroup>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Limits & Quotas Tab */}
                {tabValue === 5 && (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Resource Limits
                                    </Typography>

                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Max Functions Per User"
                                                type="number"
                                                value={settings.maxFunctionsPerUser}
                                                onChange={(e) => handleSettingChange('maxFunctionsPerUser', parseInt(e.target.value))}
                                                helperText="Maximum number of functions a user can create"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Max Executions Per Day"
                                                type="number"
                                                value={settings.maxExecutionsPerDay}
                                                onChange={(e) => handleSettingChange('maxExecutionsPerDay', parseInt(e.target.value))}
                                                helperText="Maximum function executions per day"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Max Storage Per Function"
                                                value={settings.maxStoragePerFunction}
                                                onChange={(e) => handleSettingChange('maxStoragePerFunction', e.target.value)}
                                                helperText="Maximum storage size per function (e.g., 1Gi)"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Max Execution Time (seconds)"
                                                type="number"
                                                value={settings.maxExecutionTime}
                                                onChange={(e) => handleSettingChange('maxExecutionTime', parseInt(e.target.value))}
                                                inputProps={{ min: 1, max: 900 }}
                                                helperText="Maximum allowed function execution time"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h5" gutterBottom>
                                        Current Usage
                                    </Typography>

                                    <List>
                                        <ListItem>
                                            <ListItemText primary="Total Functions" secondary="23 / 50" />
                                            <Box sx={{ width: 100 }}>
                                                <LinearProgress variant="determinate" value={46} />
                                            </Box>
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Executions Today" secondary="45,320 / 100,000" />
                                            <Box sx={{ width: 100 }}>
                                                <LinearProgress variant="determinate" value={45} />
                                            </Box>
                                        </ListItem>
                                        <Divider />
                                        <ListItem>
                                            <ListItemText primary="Total Storage" secondary="15.2 GB / 50 GB" />
                                            <Box sx={{ width: 100 }}>
                                                <LinearProgress variant="determinate" value={30} />
                                            </Box>
                                        </ListItem>
                                    </List>

                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="caption">Usage resets daily at 00:00 UTC</Typography>
                                    </Alert>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Save Button */}
                <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button variant="contained" size="large" onClick={handleSaveSettings} startIcon={<IconCircleCheck />}>
                        Save Settings
                    </Button>
                </Box>
            </MainCard>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default LambdaSettings;
